import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Search, Eye, Filter, LayoutGrid, LayoutList,
  MapPin, Mail, Phone, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, RefreshCw, X, TrendingUp, Shield,
  ChevronDown, ChevronUp, Download
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getRepAssociates, getRepAssociateFieldOptions, removeRepresentativeToken, getRepAssociatesExportColumns, downloadRepAssociatesExport } from '../services/representativeService';
import AssociativeAppShell from '../components/AssociativeAppShell';
import { getProvinceName } from '../utils/provinceNames';

// Accento blu/indigo di sezione per "Associati" (NON uniformato al verde della shell)
const ACCENT = '#2456A8';

interface Associate {
  _id: string;
  codice: string;
  stato: string;
  categoria: string;
  integrativa?: string;
  integrativeArray?: string[];
  nominativo: string;
  partitaIVA: string;
  email: string;
  telefono: string;
  rappresentante?: string;
  rappresentanteNome?: string;
  rappresentanteCognome?: string;
  provincia: string;
  regione: string;
  citta: string;
  iscrizione: string;
  statoTesseramento?: string;
  certifiedNetworkStatus?: {
    isInNetwork: boolean;
    status: string | null;
    certificateStatus: string | null;
    isActive: boolean;
  };
}

interface Stats {
  total: number;
  associati: number;
  futuriAssociati: number;
  inIscrizione: number;
  inRecesso: number;
  studi: number;
  autoscuole: number;
  nautica: number;
  studiIntegrAuto: number;
  studiIntegrNautica: number;
  autoIntegrStudio: number;
  autoIntegrNautica: number;
  nauticaIntegrStudio: number;
  nauticaIntegrAuto: number;
  totaleTesseramentoAttivo: number;
}

interface Scope {
  level: 'nazionale' | 'regionale' | 'provinciale';
  regione: string | null;
  provincia: string | null;
  regioni: string[]; // regioni di competenza (regione = la prima)
  province: string[]; // province di competenza (provincia = la prima)
  settori: string[]; // lista di categorie autorizzate, es. ['STUDIO'] | ['STUDIO','AUTOSCUOLA','NAUTICA']
}

// Ripiego sui campi singoli se la risposta non porta gli elenchi
const normalizzaScope = (raw: Omit<Scope, 'regioni' | 'province'> & Partial<Pick<Scope, 'regioni' | 'province'>>): Scope => ({
  ...raw,
  regioni: raw.regioni ?? (raw.regione ? [raw.regione] : []),
  province: raw.province ?? (raw.provincia ? [raw.provincia] : []),
});

type ViewMode = 'table' | 'grid';
type ItemsPerPage = 50 | 100 | 200 | 500 | 1000;

const provincePerRegione: Record<string, string[]> = {
  'Abruzzo': ['AQ', 'CH', 'PE', 'TE'],
  'Basilicata': ['MT', 'PZ'],
  'Calabria': ['CS', 'CZ', 'KR', 'RC', 'VV'],
  'Campania': ['AV', 'BN', 'CE', 'NA', 'SA'],
  'Emilia-Romagna': ['BO', 'FC', 'FE', 'MO', 'PC', 'PR', 'RA', 'RE', 'RN'],
  'Friuli-Venezia Giulia': ['GO', 'PN', 'TS', 'UD'],
  'Lazio': ['FR', 'LT', 'RI', 'RM', 'VT'],
  'Liguria': ['GE', 'IM', 'SP', 'SV'],
  'Lombardia': ['BG', 'BS', 'CO', 'CR', 'LC', 'LO', 'MB', 'MI', 'MN', 'PV', 'SO', 'VA'],
  'Marche': ['AN', 'AP', 'FM', 'MC', 'PU'],
  'Molise': ['CB', 'IS'],
  'Piemonte': ['AL', 'AT', 'BI', 'CN', 'NO', 'TO', 'VB', 'VC'],
  'Puglia': ['BA', 'BR', 'BT', 'FG', 'LE', 'TA'],
  'Sardegna': ['CA', 'NU', 'OR', 'SS', 'SU'],
  'Sicilia': ['AG', 'CL', 'CT', 'EN', 'ME', 'PA', 'RG', 'SR', 'TP'],
  'Toscana': ['AR', 'FI', 'GR', 'LI', 'LU', 'MS', 'PI', 'PO', 'PT', 'SI'],
  'Trentino-Alto Adige': ['BZ', 'TN'],
  'Umbria': ['PG', 'TR'],
  'Valle d\'Aosta': ['AO'],
  'Veneto': ['BL', 'PD', 'RO', 'TV', 'VE', 'VI', 'VR']
};

const regioni = Object.keys(provincePerRegione).sort();

const AssociativeAssociatesList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [associates, setAssociates] = useState<Associate[]>([]);
  const [stats, setStats] = useState<Stats>({
    total: 0, associati: 0, futuriAssociati: 0, inIscrizione: 0, inRecesso: 0,
    studi: 0, autoscuole: 0, nautica: 0,
    studiIntegrAuto: 0, studiIntegrNautica: 0, autoIntegrStudio: 0,
    autoIntegrNautica: 0, nauticaIntegrStudio: 0, nauticaIntegrAuto: 0,
    totaleTesseramentoAttivo: 0
  });
  const [loading, setLoading] = useState(true);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope>({ level: 'nazionale', regione: null, provincia: null, regioni: [], province: [], settori: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filters
  const [filterStato, setFilterStato] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterProvincia, setFilterProvincia] = useState('');
  const [filterRegione, setFilterRegione] = useState('');
  const [filterStatoTesseramento, setFilterStatoTesseramento] = useState('');
  const [filterReteCertificata, setFilterReteCertificata] = useState('');
  const [filterIntegrativa, setFilterIntegrativa] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPage>(50);

  // UI
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [showFilters, setShowFilters] = useState(false);
  const [showStats, setShowStats] = useState(true);

  // Export modal
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportColumns, setExportColumns] = useState<Array<{ key: string; label: string; group: string }>>([]);
  const [selectedExportKeys, setSelectedExportKeys] = useState<Set<string>>(new Set());
  const [exportColumnsLoaded, setExportColumnsLoaded] = useState(false);
  const [exportAll, setExportAll] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Field options
  const [fieldOptions, setFieldOptions] = useState<{
    stati: Array<{ _id: string; originalValue: string | null; count: number; isEmpty: boolean }>;
    tesseramenti: Array<{ _id: string; originalValue: string | null; count: number; isEmpty: boolean }>;
    categorie: Array<{ _id: string; originalValue: string | null; count: number; isEmpty: boolean }>;
    integrative: Array<{ _id: string; originalValue: string | null; count: number; isEmpty: boolean }>;
  }>({ stati: [], tesseramenti: [], categorie: [], integrative: [] });

  // Province filtrate in base alla regione selezionata
  const filteredProvinces = filterRegione
    ? (provincePerRegione[filterRegione] || [])
    : [];

  // Conteggio filtri attivi
  const activeFiltersCount = [filterStato, filterCategoria, filterRegione, filterProvincia, filterStatoTesseramento, filterReteCertificata, filterIntegrativa, searchTerm]
    .filter(Boolean).length;

  // Token check + load field options
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }

    const fetchFieldOptions = async () => {
      try {
        const res = await getRepAssociateFieldOptions();
        if (res.success) {
          setFieldOptions(res.options);
          setScope(normalizzaScope(res.scope));
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        if (err.response?.status === 403) {
          // Nessun permesso scope.* o settore.* assegnato. Mostra messaggio in pagina, niente logout.
          setPermissionError(err.response?.data?.error || 'Permessi insufficienti per accedere alla lista associati.');
          setLoading(false);
          return;
        }
        console.error('Errore caricamento opzioni filtri:', err);
      }
    };

    fetchFieldOptions();
  }, [navigate]);

  // Reset provincia se non valida per la regione
  useEffect(() => {
    if (filterRegione && filterProvincia) {
      const provinceDellaRegione = provincePerRegione[filterRegione] || [];
      if (!provinceDellaRegione.includes(filterProvincia)) {
        setFilterProvincia('');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRegione]);

  // Debounce search to avoid refetch on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch associates
  useEffect(() => {
    fetchAssociates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, filterStato, filterCategoria, filterProvincia, filterRegione, filterStatoTesseramento, filterReteCertificata, filterIntegrativa, itemsPerPage]);

  const fetchAssociates = async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterStato) params.stato = filterStato;
      if (filterCategoria) params.categoria = filterCategoria;
      if (filterProvincia) params.provincia = filterProvincia;
      if (filterRegione) params.regione = filterRegione;
      if (filterStatoTesseramento) params.statoTesseramento = filterStatoTesseramento;
      if (filterReteCertificata) params.isInCertifiedNetwork = filterReteCertificata;
      if (filterIntegrativa) params.integrativa = filterIntegrativa;

      const res = await getRepAssociates(params);
      setAssociates(res.data);
      setStats(res.stats);
      setScope(normalizzaScope(res.scope));
      setTotalPages(res.pagination.pages);
      setTotalItems(res.pagination.total);
    } catch (err: any) {
      if (err.response?.status === 401) {
        removeRepresentativeToken();
        navigate('/accesso-associati');
        return;
      }
      if (err.response?.status === 403) {
        setPermissionError(err.response?.data?.error || 'Permessi insufficienti per accedere alla lista associati.');
        return;
      }
      console.error('Errore caricamento associati:', err);
      toast.error('Errore nel caricamento degli associati');
    } finally {
      setLoading(false);
    }
  };

  // ===== Export Excel =====
  const openExportModal = async () => {
    if (!exportColumnsLoaded) {
      try {
        const data = await getRepAssociatesExportColumns();
        const cols: Array<{ key: string; label: string; group: string }> = data.columns;
        setExportColumns(cols);
        const saved = localStorage.getItem('repAssociatesExportColumns');
        if (saved) {
          try {
            const savedKeys: string[] = JSON.parse(saved);
            const validKeys = new Set(cols.map(c => c.key));
            setSelectedExportKeys(new Set(savedKeys.filter(k => validKeys.has(k))));
          } catch {
            setSelectedExportKeys(new Set(cols.map(c => c.key)));
          }
        } else {
          setSelectedExportKeys(new Set(cols.map(c => c.key)));
        }
        setExportColumnsLoaded(true);
      } catch {
        toast.error('Errore caricamento colonne export');
        return;
      }
    }
    setShowExportModal(true);
  };

  const buildExportFilterParams = (): Record<string, string> => {
    const p: Record<string, string> = {};
    if (debouncedSearch) p.search = debouncedSearch;
    if (filterStato) p.stato = filterStato;
    if (filterCategoria) p.categoria = filterCategoria;
    if (filterProvincia) p.provincia = filterProvincia;
    if (filterRegione) p.regione = filterRegione;
    if (filterStatoTesseramento) p.statoTesseramento = filterStatoTesseramento;
    if (filterReteCertificata) p.isInCertifiedNetwork = filterReteCertificata;
    if (filterIntegrativa) p.integrativa = filterIntegrativa;
    return p;
  };

  const executeExport = async () => {
    if (selectedExportKeys.size === 0) {
      toast.error('Seleziona almeno una colonna');
      return;
    }
    localStorage.setItem('repAssociatesExportColumns', JSON.stringify(Array.from(selectedExportKeys)));
    setExporting(true);
    try {
      const params: Record<string, string> = {
        columns: Array.from(selectedExportKeys).join(','),
        exportAll: exportAll ? '1' : '0',
        ...(!exportAll ? buildExportFilterParams() : {})
      };
      const res = await downloadRepAssociatesExport(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'];
      let filename = `associati_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (cd) {
        const m = cd.match(/filename="?([^"]+)"?/);
        if (m && m[1]) filename = m[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowExportModal(false);
      toast.success(`Export ${exportAll ? 'completo' : 'filtrato'} scaricato`);
    } catch {
      toast.error('Errore durante l\'export Excel');
    } finally {
      setExporting(false);
    }
  };

  const toggleExportGroup = (group: string) => {
    const groupKeys = exportColumns.filter(c => c.group === group).map(c => c.key);
    const allSelected = groupKeys.every(k => selectedExportKeys.has(k));
    const next = new Set(selectedExportKeys);
    groupKeys.forEach(k => { if (allSelected) next.delete(k); else next.add(k); });
    setSelectedExportKeys(next);
  };

  const exportColumnsByGroup = (() => {
    const map: Record<string, Array<{ key: string; label: string; group: string }>> = {};
    exportColumns.forEach(c => { (map[c.group] ||= []).push(c); });
    return map;
  })();

  const clearAllFilters = () => {
    setFilterStato('');
    setFilterCategoria('');
    setFilterProvincia('');
    setFilterRegione('');
    setFilterStatoTesseramento('');
    setFilterReteCertificata('');
    setFilterIntegrativa('');
    setSearchTerm('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  const getCategoriaBadge = (categoria: string, integrativeArray?: string[], integrativa?: string) => {
    const colors: Record<string, string> = {
      'STUDIO': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'AUTOSCUOLA': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'NAUTICA': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200'
    };

    const integrative = integrativeArray && integrativeArray.length > 0
      ? integrativeArray
      : integrativa ? integrativa.split(/\s+/).filter(Boolean) : [];

    return (
      <div className="flex flex-col gap-0.5">
        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${colors[categoria] || 'bg-gray-100 text-gray-800'}`}>
          {categoria}
        </span>
        {integrative.map((item, idx) => (
          <span key={idx} className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${colors[item] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
            <span className="inline-flex items-center justify-center w-3 h-3 rounded-full bg-white/60 dark:bg-black/20 text-[8px] font-bold leading-none">i</span>
            {item}
          </span>
        ))}
      </div>
    );
  };

  // Calcola range visualizzato
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  // Label settori: una sola categoria → mostra il nome, più categorie → mostra '(Misto)' o nulla
  const SETTORE_LABEL: Record<string, string> = { STUDIO: 'Studi', AUTOSCUOLA: 'Autoscuole', NAUTICA: 'Nautica' };
  const settoreLabel = scope.settori.length === 1
    ? ` (${SETTORE_LABEL[scope.settori[0]] || scope.settori[0]})`
    : scope.settori.length > 1 && scope.settori.length < 3
      ? ` (${scope.settori.map(s => SETTORE_LABEL[s] || s).join(' + ')})`
      : '';
  // Territorio solo se l'elenco non è vuoto (niente «in » tronco)
  const territorioLabel = scope.level === 'regionale' && scope.regioni.length > 0
    ? ` in ${scope.regioni.join(', ')}`
    : scope.level === 'provinciale' && scope.province.length > 1
    ? ` nelle province di ${scope.province.map(getProvinceName).join(', ')}`
    : scope.level === 'provinciale' && scope.province.length === 1
    ? ` in provincia di ${getProvinceName(scope.province[0])}`
    : '';
  const scopeLabel = `${totalItems.toLocaleString()} associati${territorioLabel}${settoreLabel}`;

  // Categoria selectable solo se il rappresentante ha più settori autorizzati
  // (con 1 settore solo è gia implicito nel filtro).
  const showCategoriaFilter = scope.settori.length !== 1;

  // Regione: tutte per il nazionale, le regioni di competenza per il regionale con più regioni
  const showRegioneFilter = scope.level === 'nazionale' || (scope.level === 'regionale' && scope.regioni.length > 1);
  const regioniFiltro = scope.level === 'nazionale' ? regioni : scope.regioni;
  const showProvinciaFilter = scope.level === 'nazionale' || scope.level === 'regionale'
    || (scope.level === 'provinciale' && scope.province.length > 1);
  // Regionale: province della regione filtrata, altrimenti di tutte le regioni di competenza;
  // provinciale con più province: le province di competenza
  const provinceFiltro = scope.level === 'regionale'
    ? (filterRegione
      ? (provincePerRegione[filterRegione] || [])
      : Array.from(new Set(scope.regioni.flatMap(r => provincePerRegione[r] || []))))
    : scope.level === 'provinciale'
    ? scope.province
    : filteredProvinces;

  // Tailwind JIT requires full class names — no string interpolation
  const FILTER_GRID_COLS: Record<number, string> = { 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5', 6: 'lg:grid-cols-6', 7: 'lg:grid-cols-7' };
  const filterGridCols = FILTER_GRID_COLS[4 + [showCategoriaFilter, showRegioneFilter, showProvinciaFilter].filter(Boolean).length];

  if (permissionError) {
    return (
      <AssociativeAppShell route="associati" associatesAccess={true}>
        <div className="page fade-in">
          <div className="max-w-2xl mx-auto py-16 px-4">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
              <h2 className="text-base font-semibold text-amber-900 dark:text-amber-100 mb-2">Accesso non disponibile</h2>
              <p className="text-sm text-amber-800 dark:text-amber-200 mb-4">{permissionError}</p>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Le tue cariche attive non hanno ancora un ambito territoriale (provincia/regione/nazionale) e/o di settore (studi/autoscuole/nautica) configurato.
                Contatta la segreteria UNASCA per richiedere l'assegnazione dei permessi.
              </p>
            </div>
          </div>
        </div>
      </AssociativeAppShell>
    );
  }

  if (loading && associates.length === 0) {
    return (
      <AssociativeAppShell route="associati" associatesAccess={true}>
        <div className="page fade-in">
          <div className="muted" style={{ textAlign: 'center', padding: '80px 0', fontSize: '13px' }}>Caricamento associati…</div>
        </div>
      </AssociativeAppShell>
    );
  }

  return (
    <AssociativeAppShell route="associati" associatesAccess={true}>
      <div className="page fade-in">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Header (accento blu/indigo di sezione) */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: 'var(--r-md)', background: 'rgba(36,86,168,.12)', color: ACCENT, display: 'grid', placeItems: 'center', flex: '0 0 auto' }}>
              <Users size={22} />
            </div>
            <div>
              <div className="mono-label" style={{ color: ACCENT }}>Associati</div>
              <h1 style={{ margin: '4px 0 0', font: '700 26px var(--font-display)', letterSpacing: 'var(--tracking-tight)', color: 'var(--text-strong)' }}>Associati</h1>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: '13px' }}>{scopeLabel}</p>
            </div>
          </div>
          <Link to="/area-associativa" className="back-btn">
            <ChevronLeft size={15} />
            Torna alla dashboard
          </Link>
        </div>

        {/* Statistiche → griglia KPI (numeri reali) */}
        <div>
          <button
            onClick={() => setShowStats(!showStats)}
            className="mono-label"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', background: 'none', border: 'none', cursor: 'pointer', color: ACCENT, marginBottom: '12px', padding: 0 }}
          >
            <TrendingUp size={13} />
            Statistiche
            {showStats ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>

          {showStats && (
            <div className="kpis" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))' }}>
              <div className="kpi">
                <div className="n">{stats.total.toLocaleString()}</div>
                <div className="l">Totale</div>
              </div>
              <div className="kpi">
                <div className="n"><em>{stats.associati.toLocaleString()}</em></div>
                <div className="l">Associati</div>
              </div>
              <div className="kpi">
                <div className="n">{stats.futuriAssociati.toLocaleString()}</div>
                <div className="l">Futuri</div>
              </div>
              <div className="kpi">
                <div className="n">{stats.inIscrizione.toLocaleString()}</div>
                <div className="l">Iscrizione</div>
              </div>
              <div className="kpi">
                <div className="n">{stats.inRecesso.toLocaleString()}</div>
                <div className="l">Recesso</div>
              </div>
              <div className="kpi">
                <div className="n"><em>{stats.totaleTesseramentoAttivo.toLocaleString()}</em></div>
                <div className="l">Attivi</div>
              </div>
              <div className="kpi">
                <div className="n">{stats.studi.toLocaleString()}</div>
                <div className="l">Studi</div>
                {(stats.studiIntegrAuto > 0 || stats.studiIntegrNautica > 0) && (
                  <div className="muted" style={{ fontSize: '10px', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {stats.studiIntegrAuto > 0 && <span>+{stats.studiIntegrAuto} i.Auto</span>}
                    {stats.studiIntegrNautica > 0 && <span>+{stats.studiIntegrNautica} i.Naut</span>}
                  </div>
                )}
              </div>
              <div className="kpi">
                <div className="n">{stats.autoscuole.toLocaleString()}</div>
                <div className="l">Auto</div>
                {(stats.autoIntegrStudio > 0 || stats.autoIntegrNautica > 0) && (
                  <div className="muted" style={{ fontSize: '10px', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {stats.autoIntegrStudio > 0 && <span>+{stats.autoIntegrStudio} i.Stud</span>}
                    {stats.autoIntegrNautica > 0 && <span>+{stats.autoIntegrNautica} i.Naut</span>}
                  </div>
                )}
              </div>
              <div className="kpi">
                <div className="n">{stats.nautica.toLocaleString()}</div>
                <div className="l">Nautica</div>
                {(stats.nauticaIntegrStudio > 0 || stats.nauticaIntegrAuto > 0) && (
                  <div className="muted" style={{ fontSize: '10px', marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {stats.nauticaIntegrStudio > 0 && <span>+{stats.nauticaIntegrStudio} i.Stud</span>}
                    {stats.nauticaIntegrAuto > 0 && <span>+{stats.nauticaIntegrAuto} i.Auto</span>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div>
          <div className="toolbar">
            <div className="search">
              <Search size={15} />
              <input
                type="text"
                placeholder="Cerca per nome, codice, P.IVA, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => { setSearchTerm(''); setDebouncedSearch(''); setCurrentPage(1); }}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', display: 'inline-flex', padding: 0 }}
                  title="Pulisci ricerca"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            <div className="seg">
              <button
                type="button"
                aria-pressed={viewMode === 'table'}
                onClick={() => setViewMode('table')}
                title="Vista tabella"
              >
                <LayoutList size={15} />
              </button>
              <button
                type="button"
                aria-pressed={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
                title="Vista griglia"
              >
                <LayoutGrid size={15} />
              </button>
            </div>

            <button
              type="button"
              className={`btn ${showFilters || activeFiltersCount > 0 ? 'soft' : 'ghost'}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter size={15} />
              Filtri
              {activeFiltersCount > 0 && (
                <span style={{ background: ACCENT, color: '#fff', borderRadius: 'var(--r-pill)', fontSize: '10px', fontWeight: 600, padding: '1px 7px', lineHeight: 1.6 }}>{activeFiltersCount}</span>
              )}
            </button>

            {activeFiltersCount > 0 && (
              <button type="button" className="btn ghost" onClick={clearAllFilters} title="Rimuovi tutti i filtri">
                <RefreshCw size={15} />
                Reset
              </button>
            )}

            <button type="button" className="btn primary" onClick={openExportModal} title="Esporta in Excel con selezione colonne">
              <Download size={15} />
              Esporta
            </button>
          </div>

          {/* Filtri espandibili */}
          {showFilters && (
            <section className="card" style={{ marginTop: '14px', padding: '18px 20px' }}>
              <div className={`grid grid-cols-2 md:grid-cols-3 ${filterGridCols} gap-3`}>
                <div>
                  <label className="field-label">Stato</label>
                  <select
                    value={filterStato}
                    onChange={(e) => { setFilterStato(e.target.value); setCurrentPage(1); }}
                    className="select-pill"
                    style={{ width: '100%' }}
                  >
                    <option value="">Tutti</option>
                    {fieldOptions.stati.map(s => (
                      <option key={s._id} value={s.isEmpty ? '__EMPTY__' : s._id}>
                        {s.isEmpty ? '(Non specificato)' : s._id} ({s.count.toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>
                {showCategoriaFilter && (
                  <div>
                    <label className="field-label">Categoria</label>
                    <select
                      value={filterCategoria}
                      onChange={(e) => { setFilterCategoria(e.target.value); setCurrentPage(1); }}
                      className="select-pill"
                      style={{ width: '100%' }}
                    >
                      <option value="">Tutte</option>
                      {fieldOptions.categorie.map(c => (
                        <option key={c._id} value={c.isEmpty ? '__EMPTY__' : c._id}>
                          {c.isEmpty ? '(Non specificata)' : c._id} ({c.count.toLocaleString()})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {showRegioneFilter && (
                  <div>
                    <label className="field-label">Regione</label>
                    <select
                      value={filterRegione}
                      onChange={(e) => { setFilterRegione(e.target.value); setCurrentPage(1); }}
                      className="select-pill"
                      style={{ width: '100%' }}
                    >
                      <option value="">Tutte</option>
                      {regioniFiltro.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                )}
                {showProvinciaFilter && (
                  <div>
                    <label className="field-label">Provincia</label>
                    <select
                      value={filterProvincia}
                      onChange={(e) => { setFilterProvincia(e.target.value); setCurrentPage(1); }}
                      className="select-pill"
                      style={{ width: '100%' }}
                      disabled={scope.level === 'nazionale' && !filterRegione && filteredProvinces.length === 0}
                    >
                      <option value="">Tutte</option>
                      {provinceFiltro.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="field-label">Tesseramento</label>
                  <select
                    value={filterStatoTesseramento}
                    onChange={(e) => { setFilterStatoTesseramento(e.target.value); setCurrentPage(1); }}
                    className="select-pill"
                    style={{ width: '100%' }}
                  >
                    <option value="">Tutti</option>
                    {fieldOptions.tesseramenti.map(t => (
                      <option key={t._id} value={t.isEmpty ? '__EMPTY__' : t._id}>
                        {t.isEmpty ? '(Non specificato)' : t._id} ({t.count.toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Rete Certificata</label>
                  <select
                    value={filterReteCertificata}
                    onChange={(e) => { setFilterReteCertificata(e.target.value); setCurrentPage(1); }}
                    className="select-pill"
                    style={{ width: '100%' }}
                  >
                    <option value="">Tutti</option>
                    <option value="true">Iscritti RC</option>
                    <option value="paying">Iscritti RC - Paganti</option>
                    <option value="not_paying">Iscritti RC - Non pagato</option>
                    <option value="false">Non iscritti</option>
                  </select>
                </div>
                <div>
                  <label className="field-label">Q. Integrativa</label>
                  <select
                    value={filterIntegrativa}
                    onChange={(e) => { setFilterIntegrativa(e.target.value); setCurrentPage(1); }}
                    className="select-pill"
                    style={{ width: '100%' }}
                  >
                    <option value="">Tutte</option>
                    <option value="__HAS_VALUE__">Con integrativa</option>
                    <option value="__EMPTY__">Senza integrativa</option>
                    {fieldOptions.integrative.map(i => (
                      <option key={i._id} value={i._id}>
                        {i._id} ({i.count.toLocaleString()})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Content */}
        <section className="card" style={{ overflow: 'hidden' }}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="w-10 h-10 border-4 border-blue-200 dark:border-blue-900 rounded-full animate-pulse"></div>
                  <div className="absolute top-0 left-0 w-10 h-10 border-4 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
                </div>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Caricamento...</p>
              </div>
            </div>
          ) : associates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full mb-3">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Nessun associato trovato</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Prova a modificare i filtri di ricerca</p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block">
                {viewMode === 'table' && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/80 dark:bg-gray-900/50">
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Codice</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nominativo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Categoria</th>
                          <th className="px-2 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell w-[90px]">Stato / Tess.</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell" title="Rete Certificata">RC</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">Rappresentante</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden xl:table-cell">Località</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                        {associates.map((associate) => (
                          <tr key={associate._id} className="group hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-sm font-mono font-medium text-gray-700 dark:text-gray-300">
                                {associate.codice}
                              </span>
                            </td>
                            <td className="px-4 py-3 max-w-[200px] lg:max-w-[280px] xl:max-w-[320px]">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={associate.nominativo}>
                                {associate.nominativo.length > 80 ? associate.nominativo.substring(0, 80) + '...' : associate.nominativo}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                <span className="font-mono text-gray-600 dark:text-gray-300">{associate.partitaIVA}</span>
                                <span className="hidden lg:flex items-center gap-1 truncate">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  {associate.email || 'N/D'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              {getCategoriaBadge(associate.categoria, associate.integrativeArray, associate.integrativa)}
                            </td>
                            <td className="px-2 py-3 hidden lg:table-cell w-[90px]">
                              <div className="flex flex-col gap-1">
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                                  associate.stato === 'ASSOCIATO' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  associate.stato === 'FUTURO ASSOCIATO' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                  associate.stato === 'IN ISCRIZIONE' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                  associate.stato === 'IN RECESSO' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                }`}>
                                  {associate.stato}
                                </span>
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                                  associate.statoTesseramento === 'PAGATO' || associate.statoTesseramento === 'ATTIVATO'
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' :
                                  associate.statoTesseramento === 'NON PAGATO'
                                    ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                                  associate.statoTesseramento === 'IN ATTIVAZIONE'
                                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' :
                                  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                  {associate.statoTesseramento || 'N/D'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden xl:table-cell text-center">
                              {associate.certifiedNetworkStatus?.isInNetwork ? (
                                associate.certifiedNetworkStatus.isActive ? (
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50" title="Rete Certificata: ATTIVO">
                                    <Shield className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/50" title={`Rete Certificata: ${associate.certifiedNetworkStatus.certificateStatus || 'NON ATTIVO'}`}>
                                    <Shield className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                                  </span>
                                )
                              ) : (
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700" title="Non iscritto alla Rete Certificata">
                                  <span className="w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500"></span>
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 hidden xl:table-cell">
                              <div>
                                {(associate.rappresentanteNome || associate.rappresentanteCognome) ? (
                                  <div className="text-sm">
                                    <span className="text-gray-700 dark:text-gray-300 font-medium">{associate.rappresentanteCognome || ''}</span>
                                    {associate.rappresentanteNome && (
                                      <span className="text-gray-500 dark:text-gray-400 ml-1">{associate.rappresentanteNome}</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400 dark:text-gray-500 truncate block max-w-[180px]" title={associate.rappresentante || ''}>
                                    {associate.rappresentante || '-'}
                                  </span>
                                )}
                                {associate.telefono && (
                                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    <Phone className="w-3 h-3 flex-shrink-0" />
                                    <span>{associate.telefono}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden xl:table-cell">
                              <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="truncate">{associate.citta}</span>
                                <span className="text-gray-400">({associate.provincia})</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end">
                                <Link
                                  to={`/area-associativa/associati/${associate._id}`}
                                  className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all"
                                  title="Visualizza dettagli"
                                >
                                  <Eye className="w-5 h-5" />
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Grid View */}
                {viewMode === 'grid' && (
                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {associates.map((associate) => (
                      <div
                        key={associate._id}
                        className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-200 hover:-translate-y-0.5"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-base shadow-lg shadow-blue-500/25">
                            {associate.nominativo?.charAt(0) || '?'}
                          </div>
                          <span className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-xs font-mono font-medium text-gray-600 dark:text-gray-400">
                            {associate.codice}
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-2 line-clamp-2">{associate.nominativo}</h3>
                        <p className="text-xs font-mono text-gray-600 dark:text-gray-400 mb-3">P.IVA: {associate.partitaIVA}</p>

                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {getCategoriaBadge(associate.categoria, associate.integrativeArray, associate.integrativa)}
                          <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${
                            associate.stato === 'ASSOCIATO' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                            associate.stato === 'FUTURO ASSOCIATO' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                            associate.stato === 'IN ISCRIZIONE' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                            associate.stato === 'IN RECESSO' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {associate.stato}
                          </span>
                          <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${
                            associate.statoTesseramento === 'PAGATO' || associate.statoTesseramento === 'ATTIVATO'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' :
                            associate.statoTesseramento === 'NON PAGATO'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' :
                            associate.statoTesseramento === 'IN ATTIVAZIONE'
                              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {associate.statoTesseramento || 'N/D'}
                          </span>
                        </div>

                        <div className="space-y-1.5 mb-3 text-xs text-gray-600 dark:text-gray-400">
                          {associate.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="truncate">{associate.email}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{associate.citta} ({associate.provincia})</span>
                          </div>
                        </div>

                        <div className="flex items-center justify-end pt-3 border-t border-gray-100 dark:border-gray-700">
                          <Link
                            to={`/area-associativa/associati/${associate._id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Dettagli
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden p-3 space-y-3">
                {associates.map((associate) => (
                  <div key={associate._id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
                    <div className="flex items-start justify-between mb-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs font-mono font-medium text-gray-700 dark:text-gray-300">
                        {associate.codice}
                      </span>
                      {getCategoriaBadge(associate.categoria, associate.integrativeArray, associate.integrativa)}
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 break-words">{associate.nominativo}</h3>
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400 mb-3">P.IVA: {associate.partitaIVA}</p>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${
                        associate.stato === 'ASSOCIATO' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        associate.stato === 'FUTURO ASSOCIATO' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {associate.stato}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${
                        associate.statoTesseramento === 'PAGATO' || associate.statoTesseramento === 'ATTIVATO'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300' :
                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {associate.statoTesseramento || 'N/D'}
                      </span>
                    </div>
                    <div className="space-y-1.5 mb-3 text-xs text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{associate.citta} ({associate.provincia})</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <Link
                        to={`/area-associativa/associati/${associate._id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Visualizza
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/30">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 dark:text-gray-400">Righe:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => { setItemsPerPage(Number(e.target.value) as ItemsPerPage); setCurrentPage(1); }}
                        className="px-2 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                        <option value={1000}>1.000</option>
                      </select>
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 hidden sm:inline">
                      {startItem}-{endItem} di {totalItems.toLocaleString()}
                    </span>
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Prima pagina">
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Precedente">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-1 mx-2">
                        {getPageNumbers().map((page, i) => (
                          typeof page === 'number' ? (
                            <button
                              key={i}
                              onClick={() => setCurrentPage(page)}
                              className={`min-w-[32px] h-8 px-2.5 rounded-lg text-sm font-medium transition-all ${
                                page === currentPage
                                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {page}
                            </button>
                          ) : (
                            <span key={i} className="px-2 text-gray-400">...</span>
                          )
                        ))}
                      </div>
                      <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Successiva">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" title="Ultima pagina">
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowExportModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Download className="w-4 h-4 text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Esporta Associati in Excel</h3>
              </div>
              <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" title="Chiudi">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input type="radio" checked={exportAll} onChange={() => setExportAll(true)} className="text-emerald-600 focus:ring-emerald-500" />
                  <span><span className="font-medium">Tutti</span> gli associati visibili</span>
                </label>
                <label className={`flex items-center gap-2 text-xs cursor-pointer ${activeFiltersCount === 0 ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  <input
                    type="radio"
                    checked={!exportAll}
                    onChange={() => setExportAll(false)}
                    disabled={activeFiltersCount === 0}
                    className="text-emerald-600 focus:ring-emerald-500 disabled:opacity-50"
                  />
                  <span>Solo <span className="font-medium">filtrati</span> ({totalItems.toLocaleString()})</span>
                </label>
              </div>

              <div className="flex items-center justify-between border-t border-b border-gray-100 dark:border-gray-700 py-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-semibold text-gray-900 dark:text-white">{selectedExportKeys.size}</span> / {exportColumns.length} colonne
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedExportKeys(new Set(exportColumns.map(c => c.key)))} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">Tutto</button>
                  <span className="text-xs text-gray-300">·</span>
                  <button onClick={() => setSelectedExportKeys(new Set())} className="text-xs text-gray-500 hover:text-gray-700 font-medium">Niente</button>
                </div>
              </div>

              <div className="space-y-3">
                {Object.entries(exportColumnsByGroup).map(([group, cols]) => {
                  const allInGroup = cols.every(c => selectedExportKeys.has(c.key));
                  const someInGroup = cols.some(c => selectedExportKeys.has(c.key));
                  return (
                    <div key={group} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <button onClick={() => toggleExportGroup(group)} className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2 hover:text-emerald-600">
                        <input
                          type="checkbox"
                          checked={allInGroup}
                          ref={(el) => { if (el) el.indeterminate = !allInGroup && someInGroup; }}
                          onChange={() => toggleExportGroup(group)}
                          className="text-emerald-600 focus:ring-emerald-500"
                        />
                        <span>{group}</span>
                        <span className="text-xs font-normal text-gray-400">({cols.filter(c => selectedExportKeys.has(c.key)).length}/{cols.length})</span>
                      </button>
                      <div className="grid grid-cols-2 gap-1.5 pl-6">
                        {cols.map(c => (
                          <label key={c.key} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer hover:text-emerald-600">
                            <input
                              type="checkbox"
                              checked={selectedExportKeys.has(c.key)}
                              onChange={() => {
                                const next = new Set(selectedExportKeys);
                                if (next.has(c.key)) next.delete(c.key); else next.add(c.key);
                                setSelectedExportKeys(next);
                              }}
                              className="text-emerald-600 focus:ring-emerald-500"
                            />
                            <span>{c.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowExportModal(false)} disabled={exporting} className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">Annulla</button>
              <button onClick={executeExport} disabled={exporting || selectedExportKeys.size === 0} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <Download className="w-3.5 h-3.5" />
                {exporting ? 'Generazione...' : 'Scarica Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AssociativeAppShell>
  );
};

export default AssociativeAssociatesList;
